import { ChartData } from 'chart.js';
import { useEffect, useState } from 'react';

import { ErrorData, EscorerEventsRequest, LatencyData } from '../../../../admin-center/gen/escorer/escorer_pb';
import { useAdminCenterService } from '../../../../admin-center/hooks';
import { useUser } from '../../../../utils/hooks';
import {
  buildUserContext,
  FlatRawChartData,
  formatDateLocal,
  NestedRawChartData,
  transformFlatDataForCharts,
  transformNestedDataForCharts,
} from '../../common/utils';
import {
  eScorerComponentsColorMap,
  eScorerComponentsLabelsMap,
  EScorerErrorEntry,
  EScorerLatencyEntry,
} from './EScorer.utils';

export const useEScorerLatencyEvents = (
  startDate: Date,
  endDate: Date,
  models: string[] | undefined,
  scoreModes: string[]
) => {
  const service = useAdminCenterService();
  const [eScorerEvents, setEScorerEvents] = useState<EScorerLatencyEntry[] | null>(null);
  const [latencyChartData, setLatencyChartData] = useState<ChartData | null>(null);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const user = useUser();

  useEffect(() => {
    setLoading(true);
    const fetchEScorerLatencyEvents = async () => {
      try {
        const userContext = await buildUserContext(user);
        const request: EscorerEventsRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
          models: models === undefined || models.length ? models : [''],
          scoreModes: scoreModes.length === 0 || scoreModes.length > 0 ? scoreModes : [''],
          userContext: userContext,
        };
        const latencyEventsResponse = await service.getEscorerLatencyEvents(request);
        const latencyChartResponse = await service.getDailyEscorerLatencyCounts(request);

        const transformedLatencyEventsEvents: EScorerLatencyEntry[] =
          latencyEventsResponse.latencyData?.map((event: LatencyData) => {
            return {
              date: event.ts ? formatDateLocal(new Date(event.ts)) : '-',
              modelName: event.modelName || '-',
              scoreMode: event.scoreMode as 'batch' | 'real_time',
              dataPointsCount: event.datapointCount || 0,
              avgLatency: event.averageLatency || 0,
            };
          }) ?? [];

        const transFormedLatencyChartData = transformFlatDataForCharts(
          latencyChartResponse.data as FlatRawChartData,
          'Average Latency',
          '#6389e1'
        );
        setLatencyChartData(transFormedLatencyChartData);

        setEScorerEvents(transformedLatencyEventsEvents);
        setAvailableModels(latencyEventsResponse.availableModels || []);
      } catch (error) {
        setError('Error fetching eScorer events');
      } finally {
        setLoading(false);
      }
    };

    fetchEScorerLatencyEvents();
  }, [startDate, endDate, models, scoreModes, service, user]);

  return { eScorerEvents, availableModels, latencyChartData, loading, error };
};

export const useEScorerErrorEvents = (
  startDate: Date,
  endDate: Date,
  models: string[] | undefined,
  scoreModes: string[]
) => {
  const service = useAdminCenterService();
  const [eScorerErrorEvents, setEScorerErrorEvents] = useState<EScorerErrorEntry[] | null>(null);
  const [eScorerErrorChartData, setEScorerErrorChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const fetchEScorerErrorEvents = async () => {
      try {
        const request: EscorerEventsRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
          models: models === undefined || models.length ? models : [''],
          scoreModes: scoreModes.length ? scoreModes : [''],
        };
        const errorEventsResponse = await service.getEscorerErrorEvents(request);
        const errorChartResponse = await service.getDailyEscorerErrorAndDatapointsCounts(request);

        const transformedErrorEventsEvents: EScorerErrorEntry[] =
          errorEventsResponse.errorData?.map((event: ErrorData) => {
            return {
              date: event.ts || '-',
              modelName: event.modelName || '-',
              scoreMode: event.scoreMode as 'batch' | 'real_time',
              totalErrors: event.totalErrors || 0,
              lastErrorMsg: event.lastErrMsg || '-',
            };
          }) ?? [];

        const transformedErrorChartData = transformNestedDataForCharts(
          errorChartResponse.data as NestedRawChartData,
          eScorerComponentsLabelsMap,
          eScorerComponentsColorMap
        );
        setEScorerErrorChartData(transformedErrorChartData);
        setEScorerErrorEvents(transformedErrorEventsEvents);
      } catch (error) {
        setError('Error fetching eScorer events');
      } finally {
        setLoading(false);
      }
    };

    fetchEScorerErrorEvents();
  }, [startDate, endDate, models, scoreModes, service]);

  return { eScorerErrorEvents, eScorerErrorChartData, loading, error };
};
